// src/Components/Settings/AccountSheet.jsx
// Everything about the signed-in account in one place (Settings > Account):
// name, receipt email, password, and deleting the account. Each row opens the
// existing editor; this sheet only gathers them so Settings stays short.
import { ChevronRight, KeyRound, Mail, Trash2, UserRound } from "lucide-react";
import BottomSheet from "../BottomSheet";

const METHOD_LABELS = {
  "Google Account": "Signed in with Google",
  "Email Account": "Signed in with email",
};

function Row({ icon, title, value, onClick, destructive = false }) {
  const Icon = icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 py-3.5 text-left hover:bg-brand-surface2 -mx-2 px-2 rounded-lg transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-accent"
    >
      <span className="w-9 h-9 rounded-xl bg-brand-iconBg flex items-center justify-center flex-shrink-0" aria-hidden="true">
        <Icon size={17} className={destructive ? "text-brand-accent" : "text-brand-textMuted"} />
      </span>
      <span className="flex-1 min-w-0">
        <span className={`block text-sm font-semibold ${destructive ? "text-brand-accent" : "text-brand-text"}`}>{title}</span>
        {value && <span className="block text-xs text-brand-textSecondary truncate">{value}</span>}
      </span>
      {!destructive && <ChevronRight size={16} className="text-brand-placeholder flex-shrink-0" aria-hidden="true" />}
    </button>
  );
}

export default function AccountSheet({
  isOpen,
  onClose,
  accountInfo,
  receiptEmail,
  onEditName,
  onEditReceiptEmail,
  onPassword,
  onDeleteAccount,
}) {
  const isGoogle = accountInfo.type === "Google Account";
  const name = accountInfo.displayName || accountInfo.email || "Your account";
  const open = (action) => () => {
    onClose();
    action();
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Account" subtitle={METHOD_LABELS[accountInfo.type]}>
      <div className="flex items-center gap-3 pb-4 mb-1 border-b border-brand-borderLight">
        <span className="w-12 h-12 rounded-full bg-brand-accentMuted flex items-center justify-center flex-shrink-0 text-lg font-bold text-brand-accent" aria-hidden="true">
          {name.charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0">
          <p className="font-bold text-brand-text truncate">{name}</p>
          {accountInfo.email && <p className="text-sm text-brand-textSecondary truncate">{accountInfo.email}</p>}
        </div>
      </div>

      <div className="divide-y divide-brand-borderLight">
        <Row icon={UserRound} title="Display name" value={accountInfo.displayName || "Not set"} onClick={open(onEditName)} />
        <Row
          icon={Mail}
          title="Receipt email"
          value={receiptEmail ? `Receipts go to ${receiptEmail}` : "Where we send purchase receipts"}
          onClick={open(onEditReceiptEmail)}
        />
        <Row
          icon={KeyRound}
          title={isGoogle ? "Add a password" : "Change password"}
          value={isGoogle ? "Also sign in with your email and a password" : null}
          onClick={open(onPassword)}
        />
      </div>

      <div className="mt-4 pt-2 border-t border-brand-borderLight">
        <Row icon={Trash2} title="Delete account" onClick={open(onDeleteAccount)} destructive />
      </div>
    </BottomSheet>
  );
}
